import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Dropdown, Icon, Label, Menu, Popup, Input, Ref } from 'semantic-ui-react';

import './index.scss';
import { ClippedContent } from '../ClippedContent';

const MultipleToggableDropdown = ({
    onChange,
    nested_options,
    loading,

    values,
    placeholder,
    onHoverParent,
    searchValue,
    onSearchChange,
    isExpandable,
}) => {
    const [open, setOpen] = useState(false);
    const [categoryBeingHovered, setCategoryBeingHovered] = useState(null);
    const labelsRef = useRef(null);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);
    const [labelsHeight, setLabelsHeight] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const [visibleCount, setVisibleCount] = useState(1);
    const [searchWidth, setSearchWidth] = useState(0);
    const [measured, setMeasured] = useState(false);

    const getLabelsHeight = () => {
        if (labelsRef.current) {
            setLabelsHeight(labelsRef.current.offsetHeight);
        }
    };

    const isSelected = (id) => {
        return values.find(({ value, id: valueId }) => (value ? id === value : id === valueId));
    };

    const blurInput = () => {
        if (inputRef.current) {
            const childNodes = inputRef.current.childNodes;
            let inputNode;
            for (let i = 0; i < childNodes.length; i++) {
                if (childNodes[i].nodeName === 'INPUT') {
                    inputNode = childNodes[i];
                    break;
                }
            }
            inputNode?.blur();
        }
    };

    useEffect(() => {
        getLabelsHeight();
    });

    useEffect(() => {
        if (isExpandable) {
            function handleClickOutside(event) {
                if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                    setIsExpanded(false);
                    setCategoryBeingHovered(null);
                    setOpen(false);
                }
            }
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isExpandable]);

    useLayoutEffect(() => {
        const node = inputRef.current;
        if (!node) return;

        const getInputWidth = () => {
            const inputEl = node.querySelector('input');
            if (!inputEl) return;
            setSearchWidth(inputEl.getBoundingClientRect().width || 0);
        };

        const raf = requestAnimationFrame(getInputWidth);

        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                requestAnimationFrame(getInputWidth);
            });
            observer.observe(node);
        } else {
            window.addEventListener('resize', getInputWidth);
        }

        return () => {
            cancelAnimationFrame(raf);
            if (observer) observer.disconnect();
            else window.removeEventListener('resize', getInputWidth);
        };
    }, []);

    useLayoutEffect(() => {
        const computeVisibleCount = () => {
            if (!isExpandable || isExpanded) {
                setVisibleCount(values.length);
                setMeasured(true);
                return;
            }

            if (!searchWidth) {
                setVisibleCount(1);
                setMeasured(false);
                return;
            }

            const sampleLabels = values.map(v => v.name);
            const temp = document.createElement('canvas');
            const ctx = temp.getContext('2d');

            if (!ctx) {
                setVisibleCount(1);
                setMeasured(true);
                return;
            }

            const labelsNode = labelsRef.current;
            const computed = labelsNode ? window.getComputedStyle(labelsNode) : null;
            const font = computed
                ? `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`
                : '14px sans-serif';

            ctx.font = font;

            const gap = 8;
            const paddingAndIcon = 34;
            const ellipsisWidth = 28;
            let used = 0;
            let count = 0;

            for (const text of sampleLabels) {
                const textWidth = ctx.measureText(text).width + paddingAndIcon;
                const totalItemWidth = textWidth + (count > 0 ? gap : 0);

                if (used + totalItemWidth + ellipsisWidth <= searchWidth) {
                    used += totalItemWidth;
                    count += 1;
                } else {
                    break;
                }
            }

            setVisibleCount(Math.max(count, 1));
            setMeasured(true);
        };

        const raf = requestAnimationFrame(computeVisibleCount);
        return () => cancelAnimationFrame(raf);
    }, [values, isExpandable, isExpanded, searchWidth]);

    const visibleValues =
        isExpandable && !isExpanded
            ? values.slice(0, Math.max(visibleCount, 1))
            : values;

    const showEllipsis = isExpandable && !isExpanded && measured && visibleCount < values.length;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', minWidth: 250 }}>
            <Dropdown
                style={{ height: values.length > 0 ? labelsHeight + 30 : 35 }}
                scrolling
                className='MultipleToggableDropdown'
                onClose={() => {
                    setOpen(false);
                    setCategoryBeingHovered(null);
                }}
                value={values}
                open={open}
                loading={loading}
                multiple
                icon={<Icon size='small' className='fa-sharp fa-solid fa-caret-down NestedDropdown__icon' />}
                closeOnBlur={false}
            >
                <Dropdown.Menu
                    style={{ maxHeight: 150, overflowY: 'auto' }}
                    onScroll={() => {
                        if (categoryBeingHovered !== null) {
                            setCategoryBeingHovered(null);
                        }
                    }}
                >
                    {nested_options
                        .filter(option => !searchValue?.trim() || option.text.toLowerCase().includes(searchValue?.toLowerCase()))
                        .map(category => {
                            const isCategorySelected = isSelected(category.value);

                            return searchValue ? (
                                <Dropdown.Item
                                    text={category.text}
                                    value={category.value}
                                    key={category.key}
                                    disabled={isCategorySelected}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        if (!values.some(v => v.value === category.value)) {
                                            onChange(e, {
                                                value: [
                                                    ...values,
                                                    {
                                                        value: category.value,
                                                        name: category.parent ? `${category.parent}/${category.text}` : category.text,
                                                    },
                                                ],
                                            });
                                            setOpen(false);
                                            blurInput();
                                        }
                                    }}
                                />
                            ) : (
                                <Popup
                                    className='MultipleToggableDropdown__child-options'
                                    style={{ position: 'absolute', top: '-1em', right: '-5em', maxHeight: 150 }}
                                    position='right center'
                                    trigger={(
                                        <Dropdown.Item
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                if (!isCategorySelected && !values.some(v => v.value === category.value)) {
                                                    onChange(e, { value: [...values, { value: category.value, name: category.text }] });
                                                    setCategoryBeingHovered(null);
                                                    setOpen(false);
                                                }
                                            }}
                                            selected={categoryBeingHovered === category.key}
                                            key={category.key}
                                            disabled={category.hasOptions ? false : isCategorySelected}
                                        >
                                            {category.hasOptions ? (
                                                <Dropdown
                                                    simple
                                                    fluid
                                                    key={category.key}
                                                    text={category.text}
                                                    open={false}
                                                    loading={category.loading}
                                                    disabled={isCategorySelected}
                                                />
                                            ) : (
                                                category.text
                                            )}
                                        </Dropdown.Item>
                                    )}
                                    open={!category.disabled && categoryBeingHovered === category.key}
                                    onOpen={() => {
                                        if (!category.disabled) {
                                            setCategoryBeingHovered(category.key);
                                        }
                                        onHoverParent?.(category.key);
                                    }}
                                    {...(category?.options?.length
                                        ? {
                                            content: (
                                                <Menu secondary borderless vertical>
                                                    {(category.options || []).map(option => {
                                                        const isOptionSelected = isSelected(option.value);

                                                        return (
                                                            <Menu.Item
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                                onMouseDown={(e) => {
                                                                    e.stopPropagation();
                                                                    if (!isOptionSelected && !values.some(v => v.value === option.value)) {
                                                                        onChange(e, {
                                                                            value: [...values, { value: option.value, name: `${category.text}/${option.text}` }],
                                                                        });
                                                                        setCategoryBeingHovered(null);
                                                                        setOpen(false);
                                                                    }
                                                                }}
                                                                key={option.id}
                                                                disabled={isOptionSelected}
                                                            >
                                                                {option.text}
                                                            </Menu.Item>
                                                        );
                                                    })}
                                                </Menu>
                                            ),
                                        }
                                        : {})}
                                />
                            );
                        })}
                </Dropdown.Menu>
            </Dropdown>

            <Ref innerRef={inputRef}>
                <Input
                    className="ToggableDropdown__input"
                    iconPosition='left'
                    placeholder={placeholder}
                    value={searchValue}
                    onChange={onSearchChange}
                    onFocus={() => setOpen(true)}
                    onBlur={() => {
                        setCategoryBeingHovered(null);
                    }}
                />
            </Ref>

            <Icon
                name='search plus'
                className='ToggableDropdown__inputIcon'
                style={{ top: values.length > 0 ? labelsHeight + 5 : 10 }}
            />

            <div
                ref={labelsRef}
                className={`ToggableDropdown__labelsContainer ${isExpandable ? 'ToggableDropdown__labelsContainer--expandable' : ''} ${isExpanded ? 'ToggableDropdown__labelsContainer--expanded' : ''}`}
            >
                {visibleValues.map((value, idx) => (
                    <Label
                        key={idx}
                        className={`ToggableDropdown__label ${isExpandable ? 'ToggableDropdown__label--expandable' : ''}`}
                    >
                        {isExpandable ? (
                            <ClippedContent popupContent={value.name}>
                                {value.name}
                            </ClippedContent>
                        ) : (
                            value.name
                        )}
                        <Icon
                            style={{ marginLeft: '0px 0px 0px 5px' }}
                            className='fas fa-times'
                            link
                            onClick={(e) => {
                                onChange(e, { value: values.filter((selected) => selected !== value) });
                                getLabelsHeight();
                            }}
                        />
                    </Label>
                ))}

                {showEllipsis && (
                    <div
                        className='ToggableDropdown__ellipsis'
                        onClick={() => setIsExpanded(true)}
                    >
                        ...
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultipleToggableDropdown;
